import React, { useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Popper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { DictionaryWord } from 'app/types';
import {
  STATUS_ORDER,
  VOCAB_STATUSES,
  WordStatus,
  hexToRgba,
  statusColor,
  statusOf,
} from './meta';

interface StatusCellProps {
  word: DictionaryWord;
  /**
   * One-shot status setter — replaces the legacy
   * onToggleLearned / onToggleActive pair. Sends `status` directly; the BE
   * keeps the legacy booleans in sync.
   */
  onSetStatus: (id: number, status: WordStatus) => void;
}

const StatusCell: React.FC<StatusCellProps> = ({ word, onSetStatus }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const current = statusOf(word);
  // The visual "isNew" state is the NEW lifecycle bucket — we render an
  // outline `+` placeholder instead of a filled pill so it reads as
  // "no status set yet" at a glance.
  const isNew = current === 'NEW';
  const meta = VOCAB_STATUSES[current];
  const color = statusColor(current, mode);

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  const handlePick = (s: WordStatus) => {
    onSetStatus(word.id, s);
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'inline-flex', position: 'relative' }}>
      <Box
        component={'button'}
        ref={anchorRef}
        onClick={toggleOpen}
        title={isNew ? 'Встановити статус' : 'Змінити статус'}
        sx={
          isNew
            ? {
                alignItems: 'center',
                bgcolor: 'transparent',
                border: '1px dashed',
                borderColor: 'primary.tableBorder',
                borderRadius: 999,
                color: 'text.secondary',
                cursor: 'pointer',
                display: 'inline-flex',
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 700,
                gap: 0.625,
                height: 30,
                opacity: 0.75,
                pl: 1,
                pr: 1.125,
                transition: 'all .14s ease',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: 'primary.main',
                  borderStyle: 'solid',
                  color: 'primary.main',
                  opacity: 1,
                },
              }
            : {
                alignItems: 'center',
                bgcolor: hexToRgba(color, 0.13),
                border: '1px solid',
                borderColor: hexToRgba(color, 0.34),
                borderRadius: 999,
                color: 'text.primary',
                cursor: 'pointer',
                display: 'inline-flex',
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 700,
                gap: 0.875,
                height: 30,
                pl: 1.25,
                pr: 1.125,
                transition: 'all .14s ease',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: hexToRgba(color, 0.6) },
              }
        }
      >
        {isNew ? (
          <>
            <AddIcon sx={{ fontSize: 16 }} />
            {meta.label}
          </>
        ) : (
          <>
            <Box
              sx={{
                bgcolor: color,
                borderRadius: '50%',
                flex: 'none',
                height: 9,
                width: 9,
              }}
            />
            {meta.label}
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                fontSize: 16,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform .16s ease',
              }}
            />
          </>
        )}
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={'bottom-start'}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              borderRadius: 2,
              boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
              p: 0.75,
              width: 240,
            }}
          >
            {STATUS_ORDER.map((s) => {
              const isCur = current === s;
              const c = statusColor(s, mode);
              const meta = VOCAB_STATUSES[s];
              return (
                <Stack
                  key={s}
                  direction={'row'}
                  alignItems={'center'}
                  spacing={1.25}
                  onClick={() => handlePick(s)}
                  sx={{
                    bgcolor: isCur
                      ? hexToRgba(c, 0.12)
                      : 'transparent',
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    px: 1.25,
                    py: 1,
                    '&:hover': {
                      bgcolor: isCur
                        ? hexToRgba(c, 0.18)
                        : 'background.default',
                    },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: c,
                      borderRadius: '50%',
                      flex: 'none',
                      height: 10,
                      width: 10,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: isCur ? 700 : 600 }}
                    >
                      {meta.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontSize: 12,
                        lineHeight: 1.2,
                      }}
                    >
                      {meta.desc}
                    </Typography>
                  </Box>
                  {isCur && (
                    <CheckIcon
                      sx={{ color: c, flex: 'none', fontSize: 18 }}
                    />
                  )}
                </Stack>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default StatusCell;
